import { getProp } from "@cervval.js/common/src/tools/any.tools";
import { FilterOptionsParams } from "@cervval.js/common/src/tools/string.tools";
import { RenderCacheData } from "@cervval.js/common/src/ui/renderCacheData.types";
import { anyReallyAny } from "@cervval.js/common/tools";
import { CompareOperators } from "./filterExpression.definition";

/** If filter equals this string, return only empty (null or undefined) cells */
export const EMPTY_CELL_FILTER_VALUE = "!";
/** comparator for AND operation */
export const COMPARATOR_AND = /[&;]/;
/** Comparator to put when comparator is not defined in the filter */
export const EMPTY_COMPARATOR = "=";
/** Regex to detect [comparator(=,<,>,!...)][a string] */
export const STANDARD_REGEX_PARSE_FILTER = /^\s*([<>!=]*)\s*(.+)$/;

/** List of comparators (AND) or EMPTY_CELL_FILTER_VALUE or null if filter is invalid */
export type ParsedFilter = { op: CompareOperators, value: anyReallyAny }[] | typeof EMPTY_CELL_FILTER_VALUE | null;

/** 
 * A standard parser for extract operators and values from a string.
 * Need an external parser for process the value.
 * @param filterStr The entire string to process.
 * @param valueParser An external parser to parse filter value.
 * @returns A pair list of operator/value.
 */
function _parserStandard(filterStr: string, valueParser: (refValue: string) => anyReallyAny): ParsedFilter {
    const filterTrimmed = filterStr.trim();
    if (filterTrimmed.trim() === "") {
        // Quick exit empty filters
        return [];
    } else if (filterTrimmed === EMPTY_CELL_FILTER_VALUE) {
        return EMPTY_CELL_FILTER_VALUE;
    }
    const comparators: ParsedFilter = [];
    const filterValues = filterStr.split(COMPARATOR_AND);
    for (const filterValue of filterValues) {

        const result = filterValue.match(STANDARD_REGEX_PARSE_FILTER);
        if (!result || result.length !== 3) {
            // Invalid filter
            return null;
        }
        // Parse first part of the string returned by the regex
        const comparator: CompareOperators | undefined = getCompareOperators(result[1], EMPTY_COMPARATOR);
        const refValue = valueParser(result[2]);
        if (comparator == null || refValue == null || isNaN(refValue)) {
            // If filter invalid, show all rows
            return null;
        }
        // --------- Check that value matches the filter -------------
        comparators.push({ op: comparator, value: refValue });
    }
    return comparators;
}

/** 
 * A standard comparer for compare a value to a list of filter
 * Need an external converter and an external function to compare value to filter
 * @param p The input data.
 * @param filterList A pair list of operator/value as filters.
 * @param converter A converter who convert the itemValue to a typed value.
 * @param compareValue Compare the typed value to a filter.
 * @returns True if successful or no filter, false otherwise.
 */
function _comparerStandard(p: FilterOptionsParams<anyReallyAny>,
    filterList: { op: CompareOperators, value: anyReallyAny }[] | undefined,
    converter: (itemValue: anyReallyAny) => (anyReallyAny | null),
    compareValue: (value: anyReallyAny, op: CompareOperators, exp: anyReallyAny) => boolean
): boolean {
    if (filterList == null) {
        return true;
    }
    const value = converter(p.itemValue);
    if (value == null) {
        // Invalid number, we cannot keep it
        return false;
    }
    for (const filter of filterList) {
        if (!compareValue(value, filter.op, filter.value)) {
            // Exit on first expression not matched
            return false;
        }
    }
    return true;
}

/** 
 * A minimalist definition of a filterComparator which use the standard parser and the standard comparer. Based on the FilterComparator.
 * Need only a parser to parse the value of a filter, a converter to type the value to test (used into the parsedStandard) and a compare function to ... compare (used into the compareStandard).
 * @param p the input data
 * @param filterParser A parser function to parse the filter value (not operator) only. Used into the standard parser.
 * @param converter A converter function to convert filter as typed value. Used into the standard comparer.
 * @param compare A comparator function to compare input and converted value. Used into the standard comparer.
 * @param renderCacheData 
 * @returns Return true if successful or no filter, false otherwise.
 */
export function filterComparatorStandard(p: FilterOptionsParams<anyReallyAny>,
    filterParser: (refValue: string) => anyReallyAny,
    converter: (itemValue: anyReallyAny) => (anyReallyAny | undefined),
    compare: (value: anyReallyAny, expOp: CompareOperators, expValue: anyReallyAny) => (boolean),
    renderCacheData?: RenderCacheData): boolean {
    return filterComparator(p, (filterStr) => _parserStandard(filterStr, filterParser), (pOp, filter) => _comparerStandard(pOp, filter, converter, compare), renderCacheData);
}

/** 
 * Compare a FilterOptionsParams, if filter is invalid return true to display all rows  
 * Need a parser to extract operator and value and a comparator to test input with filters
 * @param p The input data to test.
 * @param parser A parser function to parse the filters string.
 * @param comparator A comparator function to compare input and filters.
 * @param renderCacheData 
 * @returns Return true if successful or no filter, false otherwise.
 */
export function filterComparator(p: FilterOptionsParams<anyReallyAny>,
    parser: (filterStr: string) => ParsedFilter,
    comparator: (p: FilterOptionsParams<anyReallyAny>, filter: { op: CompareOperators, value: anyReallyAny }[] | undefined) => boolean,
    renderCacheData?: RenderCacheData): boolean {
    // -- Get filter ----------------------------------------------------------
    const key = p.filterValue;
    let filter = renderCacheData ? getProp<ParsedFilter>(renderCacheData, key) : undefined;
    if (filter == null) {
        filter = parser(p.filterValue);
        if (renderCacheData) {
            renderCacheData[key] = filter;
        }
    }

    // -- Check value against filter ------------------------------------------
    if (filter == null) {
        // Invalid filter, always show result, this is safest for the user.
        // He will easily understand that the filter is not applied.
        return true;
    } else if (filter === EMPTY_CELL_FILTER_VALUE) {
        return p.itemValue == null;
    } else if (filter.length === 0) {
        // Quick exit for empty filter
        return true;
    }

    // -------- Split filters ---------------------------------------
    if (p.itemValue == null) {
        // Invalid number, we cannot keep it
        return false;
    }
    return comparator(p, filter);
}


/** get comparator from input, get comparatorIfEmpty if doesn't have comparator in this input  */
export function getCompareOperators(input: string, comparatorIfEmpty?: CompareOperators): CompareOperators | undefined {
    switch (input) {
        // ------------ Equal ---------------
        case "=":
        case "==":
        case "===":
            return "=";
        // ------------ Not equal -----------
        case "!":
        case "!=":
        case "<>":
            return "<>";
        // ------------ Less -----------------
        case "<":
            return "<";
        // ---------- Less or equal ----------
        case "<=":
        case "=<":
            return "<=";
        // ---------- Greater ----------------
        case ">":
            return ">";
        // ------- Greater or equal ----------
        case ">=":
        case "=>":
            return ">=";
        case "":
            return comparatorIfEmpty ?? undefined;
        default:
            return undefined;
    }
}
